import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, RefreshCw } from "lucide-react";
import { Logo, CtaButton, SecondaryButton, MonoLabel, ProgressDots } from "../components/kit";
import { SnapshotEditor, emptySnapshot, snapshotToEditor, snapshotFromEditor, snapshotEditorValid } from "../components/SnapshotEditor";
import { SiteReadField } from "../components/SiteReadField";
import { ProcessingLoader } from "../components/ProcessingLoader";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

const INDUSTRIES = [
  "Restaurant & Food", "Retail & Boutique", "Beauty & Wellness", "Fitness & Sports",
  "Health & Medical", "Real Estate", "Legal Services", "Accounting & Finance",
  "Construction & Trades", "Automotive", "Education & Coaching", "Travel & Hospitality",
  "Events & Entertainment", "Photography & Creative", "Software & Tech", "Marketing & Agencies",
  "E-commerce Brand", "Fashion & Apparel", "Home Services", "Nonprofit & Community",
];

const MODELS = [
  { value: "local_service", label: "Local service", desc: "Customers come to you, or you go to them" },
  { value: "ecommerce", label: "E-commerce", desc: "You sell products online" },
  { value: "b2b", label: "B2B", desc: "You sell to other businesses" },
  { value: "saas", label: "SaaS", desc: "Software with recurring subscriptions" },
  { value: "creator", label: "Creator", desc: "Audience, content and personal brand" },
  { value: "delivery", label: "Delivery", desc: "Prepared food or products for delivery and takeaway: ghost kitchens, dark stores, meal prep" },
];

const SCOPES = [
  { value: "local", label: "Local", desc: "My city or region" },
  { value: "national", label: "National", desc: "My whole country" },
  { value: "international", label: "International", desc: "Multiple countries" },
];

const GOALS = [
  { value: "sales", label: "Sales", desc: "Revenue in the door, now" },
  { value: "leads", label: "Leads", desc: "Qualified contacts to close later" },
  { value: "awareness", label: "Awareness", desc: "More people should know we exist" },
];

const MATURITY = [
  { key: "has_website", label: "We have a website" },
  { key: "active_socials", label: "Our social profiles are active" },
  { key: "email_list", label: "We have an email list" },
  { key: "ran_ads_before", label: "We've run paid ads before" },
];

const TONES = [
  { value: "friendly_warm", label: "Friendly & warm", desc: "Like a neighbor who knows their stuff" },
  { value: "bold_punchy", label: "Bold & punchy", desc: "Short sentences. Big claims. Backed up." },
  { value: "expert_trustworthy", label: "Expert & trustworthy", desc: "Calm authority, no hype" },
  { value: "playful_witty", label: "Playful & witty", desc: "A wink in every headline" },
  { value: "premium_minimal", label: "Premium & minimal", desc: "Less words. More weight." },
  { value: "down_to_earth", label: "Down-to-earth", desc: "Plain talk, honest prices" },
];

const COUNTRIES = [
  "Italy", "France", "Germany", "Spain", "Portugal", "Netherlands", "Belgium", "Austria",
  "Switzerland", "United Kingdom", "Ireland", "United States", "Canada", "Australia",
  "Poland", "Sweden", "Denmark", "Norway", "Finland", "Greece", "Other",
];

const DESC_PLACEHOLDER = "e.g. We help small manufacturing companies get government grants — we find the right call, prepare the application and get paid a success fee when the grant is approved. We also broker salary-backed loans for their employees through partner banks.";

const budgetFromSlider = (v) => {
  const raw = 100 * Math.pow(500, v / 100);
  if (raw < 1000) return Math.round(raw / 10) * 10;
  if (raw < 10000) return Math.round(raw / 100) * 100;
  return Math.round(raw / 500) * 500;
};

const STEP_META = [
  { label: "01. BUSINESS NAME", serif: "What's your", bold: "business called?" },
  { label: "02. BUSINESS TYPE", serif: "What kind of", bold: "business is it?" },
  { label: "03. WHAT YOU DO", serif: "Describe what you", bold: "actually do" },
  { label: "04. BUSINESS MODEL", serif: "How does the", bold: "money come in?" },
  { label: "05. MARKET", serif: "Where do your", bold: "customers live?" },
  { label: "06. PRIMARY GOAL", serif: "What should marketing", bold: "do for you first?" },
  { label: "07. BUDGET", serif: "How much can you", bold: "spend per month?" },
  { label: "08. MATURITY", serif: "What do you", bold: "already have?" },
  { label: "09. TONE OF VOICE", serif: "How should your", bold: "brand sound?" },
];

const LAST_STEP = 8;

export default function Onboarding() {
  const [phase, setPhase] = useState("wizard"); // wizard | extracting | confirm | extract_error
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    business_name: "",
    business_type: "",
    business_description: "",
    website_url: "",
    business_model: "",
    country: "",
    market_scope: "",
    primary_goal: "",
    slider: 50,
    maturity: { has_website: false, active_socials: false, email_list: false, ran_ads_before: false, pixel_installed: false },
    tone_of_voice: "",
  });
  const [snap, setSnap] = useState(emptySnapshot);
  const [siteWarn, setSiteWarn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const budget = budgetFromSlider(data.slider);
  const descLen = data.business_description.trim().length;

  const canContinue = [
    !!data.business_name.trim(),
    !!data.business_type,
    descLen >= 60,
    !!data.business_model,
    !!data.country && !!data.market_scope,
    !!data.primary_goal,
    true,
    true,
    !!data.tone_of_voice,
  ][step];

  const extract = async () => {
    setPhase("extracting");
    try {
      const { data: draft } = await api.post("/brand-brain/snapshot/extract");
      setSnap(snapshotToEditor(draft));
      setSiteWarn(draft.website_import === "failed");
      setPhase("confirm");
    } catch {
      setPhase("extract_error");
    }
  };

  const finish = async () => {
    setBusy(true);
    setError("");
    try {
      await api.put("/brand-brain", {
        business_name: data.business_name.trim(),
        business_description: data.business_description.trim(),
        website_url: data.website_url.trim() || null,
        business_type: data.business_type,
        business_model: data.business_model,
        country: data.country,
        market_scope: data.market_scope,
        primary_goal: data.primary_goal,
        monthly_budget: budget,
        maturity: data.maturity,
        tone_of_voice: data.tone_of_voice,
      });
      setBusy(false);
      await extract();
    } catch (err) {
      setBusy(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const confirmSnapshot = async () => {
    setBusy(true);
    try {
      await api.put("/brand-brain/snapshot", snapshotFromEditor(snap));
      setUser((u) => ({ ...u, onboarding_completed: true }));
      navigate(user?.plan ? "/app" : "/choose-plan", { replace: true });
    } catch {
      setBusy(false);
      setError("Couldn't save the snapshot — try again.");
    }
  };

  const next = () => (step === LAST_STEP ? finish() : setStep(step + 1));
  const meta = STEP_META[step];

  return (
    <div className="min-h-screen dot-grid flex flex-col" data-testid="onboarding-page">
      <div className="max-w-3xl w-full mx-auto px-6 py-6 flex items-center justify-between">
        <Logo className="h-5" />
        {phase === "wizard" && <ProgressDots total={9} current={step} />}
      </div>
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 pb-16 pt-6 sm:pt-0">
        {phase === "extracting" && (
          <div className="w-full max-w-2xl" data-testid="onboarding-extracting">
            <ProcessingLoader
              eyebrow="Reading your description"
              title="Reading your description…"
              description="Turning your words into a structured picture of the business. A few seconds."
              testId="onboarding-extract-loader"
            />
          </div>
        )}

        {phase === "extract_error" && (
          <div className="w-full max-w-2xl card-soft p-10 sm:p-14 text-center" data-testid="onboarding-extract-error">
            <p className="text-xl font-bold text-[#181621]">Couldn't read the description.</p>
            <p className="text-[14px] text-[#54505B] mt-1">Try again, or fill in the snapshot yourself. It takes a minute.</p>
            <div className="flex gap-2.5 mt-7 justify-center">
              <SecondaryButton onClick={() => { setSnap(emptySnapshot); setPhase("confirm"); }} data-testid="onboarding-manual-snapshot-btn">
                Fill it in myself
              </SecondaryButton>
              <CtaButton onClick={extract} data-testid="onboarding-extract-retry-btn">
                <RefreshCw size={15} strokeWidth={1.8} /> Try again
              </CtaButton>
            </div>
          </div>
        )}

        {phase === "confirm" && (
          <div className="w-full max-w-2xl card-soft p-7 sm:p-12" data-testid="onboarding-snapshot-confirm">
            <MonoLabel>BUSINESS SNAPSHOT</MonoLabel>
            <h1 className="mt-5 text-3xl sm:text-4xl leading-tight">
              <span className="font-serif-it block">Here's how I</span>
              <span className="font-extrabold tracking-[-0.02em] block">understood your business</span>
            </h1>
            <p className="text-[13.5px] text-[#54505B] mt-3">Fix anything that's off — this becomes the primary truth behind every plan I build for you.</p>
            {siteWarn && (
              <div className="flex items-start gap-2.5 text-[13px] text-[#96691E] bg-[#FDF6E7] rounded-lg px-3.5 py-2.5 mt-4" data-testid="onboarding-site-warning">
                <span className="flex-1">We couldn't read the website, but you can still build your Brand Brain using the information you provided.</span>
                <button className="font-medium underline underline-offset-2 shrink-0 hover:text-[#181621] transition-colors" onClick={extract} data-testid="onboarding-retry-site-btn">
                  Retry website import
                </button>
              </div>
            )}
            <div className="mt-7">
              <SnapshotEditor snap={snap} setSnap={setSnap} />
            </div>
            {error && (
              <p className="text-[13px] text-[#B4443C] bg-[#FBE9E7] rounded-lg px-3.5 py-2.5 mt-5" data-testid="onboarding-error">{error}</p>
            )}
            <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
              <SecondaryButton className="!py-2.5 !px-4 text-[13px]" onClick={extract} data-testid="onboarding-reextract-btn">
                <RefreshCw size={13} strokeWidth={1.8} /> Re-read my description
              </SecondaryButton>
              <CtaButton onClick={confirmSnapshot} disabled={!snapshotEditorValid(snap) || busy} data-testid="onboarding-confirm-snapshot-btn">
                {busy ? "Saving…" : "That's my business — let's go"}
                {!busy && <ArrowRight size={15} strokeWidth={1.8} />}
              </CtaButton>
            </div>
          </div>
        )}

        {phase === "wizard" && (
        <div className="w-full max-w-2xl card-soft p-7 sm:p-12" key={step}>
          <div className="fade-up">
            <MonoLabel data-testid="onboarding-step-label">{meta.label}</MonoLabel>
            <h1 className="mt-5 text-3xl sm:text-4xl leading-tight" data-testid="onboarding-step-title">
              <span className="font-serif-it block">{meta.serif}</span>
              <span className="font-extrabold tracking-[-0.02em] block">{meta.bold}</span>
            </h1>
          </div>

          <div className="mt-8 fade-up delay-1">
            {step === 0 && (
              <div data-testid="step-business-name">
                <input
                  className="field-input !text-lg"
                  value={data.business_name}
                  onChange={(e) => set("business_name", e.target.value)}
                  placeholder="The name your customers know you by"
                  autoFocus
                  data-testid="business-name-input"
                />
              </div>
            )}

            {step === 1 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-[42vh] overflow-y-auto pr-1" data-testid="step-business-type">
                {INDUSTRIES.map((ind) => (
                  <button
                    key={ind}
                    className={`option-card !py-3 !px-3.5 text-[13px] ${data.business_type === ind ? "selected" : ""}`}
                    onClick={() => set("business_type", ind)}
                    data-testid={`industry-${ind.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {ind}
                  </button>
                ))}
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-business-description">
                <p className="text-[13.5px] text-[#54505B] mb-3">
                  Services or products, who pays you, how. Write it like you'd explain it to a friend — the more specific, the sharper your plan.
                </p>
                <textarea
                  rows={7}
                  className="field-input resize-none"
                  value={data.business_description}
                  onChange={(e) => set("business_description", e.target.value)}
                  placeholder={DESC_PLACEHOLDER}
                  data-testid="business-description-input"
                />
                <p className={`text-[11.5px] mt-1.5 ${descLen >= 60 ? "text-[#4B7B4F]" : "text-[#9A9591]"}`} data-testid="description-counter">
                  {descLen}/60 characters minimum
                </p>
                <div className="mt-5 pt-5 border-t border-[#F3EFE7]">
                  <SiteReadField value={data.website_url} onChange={(v) => set("website_url", v)} />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-2.5" data-testid="step-business-model">
                {MODELS.map((m) => (
                  <button
                    key={m.value}
                    className={`option-card flex items-baseline justify-between gap-3 ${data.business_model === m.value ? "selected" : ""}`}
                    onClick={() => set("business_model", m.value)}
                    data-testid={`model-${m.value}`}
                  >
                    <span className="font-semibold">{m.label}</span>
                    <span className="text-[12px] text-[#54505B] text-right">{m.desc}</span>
                  </button>
                ))}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-6" data-testid="step-market">
                <div>
                  <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-2">Country</label>
                  <select
                    className="field-input"
                    value={data.country}
                    onChange={(e) => set("country", e.target.value)}
                    data-testid="country-select"
                  >
                    <option value="">Select your country…</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="font-mono-j text-[10px] tracking-[0.12em] text-[#54505B] uppercase block mb-2">Market scope</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    {SCOPES.map((s) => (
                      <button
                        key={s.value}
                        className={`option-card ${data.market_scope === s.value ? "selected" : ""}`}
                        onClick={() => set("market_scope", s.value)}
                        data-testid={`scope-${s.value}`}
                      >
                        <span className="font-semibold block">{s.label}</span>
                        <span className="text-[12px] text-[#54505B]">{s.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="space-y-2.5" data-testid="step-goal">
                {GOALS.map((g) => (
                  <button
                    key={g.value}
                    className={`option-card flex items-baseline justify-between gap-3 ${data.primary_goal === g.value ? "selected" : ""}`}
                    onClick={() => set("primary_goal", g.value)}
                    data-testid={`goal-${g.value}`}
                  >
                    <span className="font-semibold">{g.label}</span>
                    <span className="text-[12px] text-[#54505B] text-right">{g.desc}</span>
                  </button>
                ))}
              </div>
            )}

            {step === 6 && (
              <div data-testid="step-budget">
                <div className="text-center mb-8">
                  <span className="text-5xl font-extrabold tracking-tight" data-testid="budget-value">
                    €{budget.toLocaleString("en-US")}
                  </span>
                  <span className="text-[#9A9591] text-sm ml-2">/month</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={data.slider}
                  onChange={(e) => set("slider", Number(e.target.value))}
                  className="budget-slider"
                  data-testid="budget-slider"
                />
                <div className="flex justify-between mt-3 font-mono-j text-[10px] tracking-[0.1em] text-[#9A9591]">
                  <span>€100</span>
                  <span>€2,200</span>
                  <span>€50,000</span>
                </div>
              </div>
            )}

            {step === 7 && (
              <div className="space-y-2.5" data-testid="step-maturity">
                {MATURITY.map((m) => {
                  const on = data.maturity[m.key];
                  return (
                    <button
                      key={m.key}
                      className={`option-card flex items-center justify-between ${on ? "selected" : ""}`}
                      onClick={() => set("maturity", { ...data.maturity, [m.key]: !on })}
                      data-testid={`maturity-${m.key.replace(/_/g, "-")}`}
                    >
                      <span className="font-semibold">{m.label}</span>
                      <span
                        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${on ? "bg-[#181621] border-[#181621]" : "border-[#E7E2D6] bg-white"}`}
                      >
                        {on && <Check size={13} strokeWidth={2.5} className="text-white" />}
                      </span>
                    </button>
                  );
                })}
                <p className="text-[12px] text-[#9A9591] pt-1">Check everything that's true. Nothing checked is fine too.</p>
              </div>
            )}

            {step === 8 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="step-tone">
                {TONES.map((t) => (
                  <button
                    key={t.value}
                    className={`option-card ${data.tone_of_voice === t.value ? "selected" : ""}`}
                    onClick={() => set("tone_of_voice", t.value)}
                    data-testid={`tone-${t.value.replace(/_/g, "-")}`}
                  >
                    <span className="font-semibold block">{t.label}</span>
                    <span className="text-[12px] text-[#54505B]">{t.desc}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className="text-[13px] text-[#B4443C] bg-[#FBE9E7] rounded-lg px-3.5 py-2.5 mt-5" data-testid="onboarding-error">
              {error}
            </p>
          )}

          <div className="mt-9 flex items-center justify-between">
            <button
              className={`inline-flex items-center gap-1.5 text-sm font-medium text-[#54505B] hover:text-[#181621] transition-colors ${step === 0 ? "invisible" : ""}`}
              onClick={() => setStep(step - 1)}
              data-testid="onboarding-back-btn"
            >
              <ArrowLeft size={15} strokeWidth={1.8} /> Back
            </button>
            <CtaButton onClick={next} disabled={!canContinue || busy} data-testid="onboarding-continue-btn">
              {busy ? "Saving…" : step === LAST_STEP ? "Build my Brand Brain" : "Continue"}
              {!busy && <ArrowRight size={15} strokeWidth={1.8} />}
            </CtaButton>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
